import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { EmptyArray } from 'lib/array/arrayUtils'
import styled from 'styled-components'
import { rem } from 'polished'
import { useInView } from 'react-intersection-observer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import Sheet from 'components/utils/Sheet'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import Caption from 'LuxKit/Typography/Caption'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import clsx from 'clsx'
import GeoContext from 'contexts/geoContext'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import zIndex from 'styles/tools/z-index'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { getRegionSaveAmount } from 'luxPlus/lib/getRegionSaveAmountCopy'
import { dateDifference, isSameDay, useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import checkPersonalisedSaving from 'luxPlus/lib/checkPersonalisedSaving'
import useLuxPlusLandingActions from 'luxPlus/hooks/useLuxPlusLandingActions'
import { useLuxPlusPromoBanners } from 'luxPlus/hooks/useLuxPlusPromos'
import useLuxPlusLandingTab from 'luxPlus/hooks/useLuxPlusLandingTab'
import { fetchTotalPotentialSavings } from 'actions/LuxPlusActions'
import ModalContext from 'contexts/ModalContext'
import LuxPlusPotentialSavingsModal from '../modals/LuxPlusPotentialSavingsModal'
import LuxPlusLogo from '../Logos/LuxPlusLogo'
import LuxPlusStatCards from './LuxPlusStatCards'
import LuxPlusPromoBannerCarousel from './LuxPlusPromoBannerCarousel'
import LuxPlusLandingAboutSection from './LuxPlusLandingAboutSection'
import LuxPlusLandingBenefitsSection from './LuxPlusLandingBenefitsSection'
import LuxPlusLandingPartnerOffersTabSection from './LuxPlusLandingPartnerOffersTabSection'
import LuxPlusLandingNavigationProvider from 'luxPlus/contexts/LuxPlusLandingNavigationProvider'
import { type LuxPlusLandingNonMemberTab } from 'luxPlus/contexts/LuxPlusLandingNavigationContext'
import focusStyle from 'styles/utils/focusStyle'
import variables from 'styles/vendor/bootstrap/variables'
import { scrollToId } from 'lib/window/scrollUtils'

const HeroSheet = styled(Sheet)`
  background: linear-gradient(90deg, ${props => props.theme.palette.product.luxPlus.background} 0%, #4C82DF 100%);
`

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(32)};
  align-items: center;

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
  }
`

const HeroLayoutContainer = styled(LayoutContainer)`
  padding-inline: 0;

  ${mediaQueryUp.desktop} {
    padding-inline: ${rem(variables.gutterWidth / 2)};
  }
`

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    align-items: flex-start;
  }
`

const StatCardsWrapper = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    display: flex;
    flex-direction: column;
    gap: ${rem(24)};
  }
`

const MobileStatCards = styled.div`
  display: block;

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const Footnote = styled(Caption)`
  opacity: 0.8;
  max-width: ${rem(300)};
  margin: 0 auto;
  text-align: center;

  ${mediaQueryUp.tablet} {
    max-width: none;
  }
`

const PreviewInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(6)};
  padding: ${rem(16)};
  border: 1px solid ${props => props.theme.palette.highlight.primary.normalForeground};
  border-radius: ${props => props.theme.borderRadius.M};
  backdrop-filter: blur(10px);
  width: 100%;
  max-width: ${rem(350)};
  
  ${mediaQueryUp.tablet} {
    max-width: ${rem(480)};
  }
`

const NavSection = styled(Pane)`
  position: sticky;
  z-index: ${zIndex.stickyHeaderBanner};
  top: 0;
  padding: ${rem(32)} 0 ${rem(20)} 0;
`

const ViewToggle = styled.div`
  display: flex;
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  border-radius: ${rem(20)};
  padding: ${rem(4)};
  gap: ${rem(4)};
`

const ToggleButton = styled.button`
  padding: ${rem(8)} ${rem(20)};
  border: none;
  border-radius: ${rem(16)};
  font-size: ${rem(14)};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: transparent;
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};

  &:hover {
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }

  &:focus-visible {
    ${focusStyle}
  }

  &.active {
    background: ${props => props.theme.palette.neutral.surface.normalBackground};
    color: ${props => props.theme.palette.neutral.surface.normalContrast};
    box-shadow: ${props => props.theme.shadow.bottom.small};

    &:hover {
      color: ${props => props.theme.palette.neutral.surface.normalContrast};
    }
  }
`

const STICKY_NAV_SCROLL_OFFSET = -100
const FREE_PREVIEW_TABS: ReadonlyArray<LuxPlusLandingNonMemberTab> = ['about', 'benefits', 'partner-offers']

function LuxPlusFreePreviewLandingPageContent() {
  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()
  const showModal = useContext(ModalContext)
  const { currentRegionCode } = useContext(GeoContext)
  const saveAmount = getRegionSaveAmount(currentRegionCode)

  const dispatch = useAppDispatch()
  const [stickyRef, notSticky] = useInView()
  const { activeTab, navigateToTab } = useLuxPlusLandingTab(FREE_PREVIEW_TABS, 'about')

  const firstName = useAppSelector(state => state.auth.account.givenName)

  const freePreviewExpiryDate = useAppSelector(
    state => state.auth.account.luxPlus.member.subscription.item?.expiryDate || '',
  )

  const totalPotentialSavings = useAppSelector(state => state.luxPlus.totalPotentialSavings.data)
  const personalSaving = totalPotentialSavings?.totalAmount
  const savingsItems: Array<App.LuxPlusPotentialSavingsItem> = totalPotentialSavings?.items ?? EmptyArray
  const { showPersonalised } = checkPersonalisedSaving(firstName, personalSaving)

  useEffect(() => {
    dispatch(fetchTotalPotentialSavings())
  }, [dispatch])

  const { handleBecomeMember, handleBecomeMemberNoJoinFee, isSpoofed } = useLuxPlusLandingActions()
  const hasSubscriptionOffers = useAppSelector(state => !!state.luxPlus.products.subscription.offers)
  const hasJoinFeeFreeOffer = useAppSelector(
    state => !!state.luxPlus.products.subscription.offers?.some(o => o.joinFeeWaived),
  )
  const promoBanners = useLuxPlusPromoBanners()

  const handlePastBookingsClick = useCallback(() => {
    if (savingsItems.length > 0 && personalSaving !== undefined) {
      showModal(<LuxPlusPotentialSavingsModal items={savingsItems} totalSaving={personalSaving} />)
    }
  }, [showModal, savingsItems, personalSaving])

  const handleTabAbout = useCallback(() => navigateToTab('about'), [navigateToTab])
  const handleTabBenefits = useCallback(() => navigateToTab('benefits'), [navigateToTab])
  const handleTabPartnerOffers = useCallback(() => {
    navigateToTab('partner-offers')
    requestAnimationFrame(() => {
      scrollToId('partner_offers_list', STICKY_NAV_SCROLL_OFFSET)
    })
  }, [navigateToTab])

  const previewPillCopy = useMemo(() => {
    const diff = dateDifference(new Date(freePreviewExpiryDate))
    const expiresToday = isSameDay(new Date(), new Date(freePreviewExpiryDate))
    const days = expiresToday ? 0 : (diff.days <= 1 ? 1 : diff.days)

    return intl.formatMessage(
      {
        id: 'ERHTeQ0VNNMIYOiSxqZZ',
        defaultMessage: '{days, plural, =0 {Preview ends today} one {Preview ends in # day} other {Preview ends in # days}}',
        description: 'Pill label showing how many days until free preview period expires',
      },
      { days },
    )
  }, [freePreviewExpiryDate, intl])

  const subheading = useMemo(() => intl.formatMessage(
    {
      id: '7Zr2eoICwSlVtCPmu03W',
      defaultMessage: 'Your free preview is available until {date} or when you make your first {programName} purchase.',
      description: 'Subheading explaining free preview availability and expiry on landing page',
    },
    { date: intlDateFormatter(freePreviewExpiryDate, INTL_DMY_CASUAL_SHORT_FORMAT), programName: LUXURY_PLUS.PROGRAM_NAME },
  ), [freePreviewExpiryDate, intlDateFormatter, intl])

  const heroHeading = intl.formatMessage({
    id: 'OMWl94qI6vHgmthGR2MI',
    defaultMessage: 'Your VIP Travel Club',
    description: 'Hero heading on the LuxPlus free preview landing page content',
  })

  const heroSubheading = intl.formatMessage({
    id: 'r1Grlvg9sKgYZW9txrwt',
    defaultMessage: 'Unpack a world of member-only benefits.',
    description: 'Hero subheading on LuxPlus free preview landing page',
  })

  const aboutTabLabel = intl.formatMessage({
    id: '44kT2MggRLwSErniPcPV',
    defaultMessage: 'About',
    description: 'Tab label for the About section on LuxPlus landing page',
  })

  const benefitsTabLabel = intl.formatMessage({
    id: '5SpkviQdtWDk6OcsoqkP',
    defaultMessage: 'Benefits',
    description: 'Tab label for the Benefits section on LuxPlus landing page',
  })

  const partnerOffersTabLabel = intl.formatMessage({
    id: 'R-9iZhfbVJvWCOce6x8A',
    defaultMessage: 'Partner Offers',
    description: 'Tab label for the Partner Offers section on LuxPlus landing page',
  })

  const personalisedFootnoteText = intl.formatMessage({
    id: 'eCw68lCh8gzdInKwPmH-',
    defaultMessage: '*Based on your actual bookings and {programName} pricing at the time of purchase.',
    description: 'Footnote disclaimer about savings calculation on LuxPlus landing page',
  }, { programName: LUXURY_PLUS.PROGRAM_NAME })

  const nonPersonalisedFootnoteText = intl.formatMessage({
    id: 'oqoDbcJUNzDwsjzSzQMq',
    defaultMessage: '*Based on savings by top 50% of active {programName} members over 12 months',
    description: 'Footnote disclaimer about savings calculation on LuxPlus landing page',
  },
  { programName: LUXURY_PLUS.PROGRAM_NAME })

  const footnoteText = showPersonalised ? personalisedFootnoteText : nonPersonalisedFootnoteText

  const becomeMemberNoJoinFeeLabel = intl.formatMessage({
    id: 'kZptru9JkmD5d-3fAtrH',
    defaultMessage: 'Become a member (no joining fee)',
    description: 'Spoof-only CTA button to start LuxPlus membership purchase without joining fee',
  })

  const becomeMemberLabel = intl.formatMessage({
    id: 'FQH1tIR8HDpYMBX6Czah',
    defaultMessage: 'Become a member',
    description: 'Button to start LuxPlus membership purchase on free preview landing page',
  })

  return (
    <>
      <HeroSheet padding="40 20" paletteType="inverse">
        <HeroLayoutContainer>
          <HeroGrid>
            <LeftColumn>
              <LuxPlusLogo height={20} />
              <HeadingTextBlock variant="heading1" align="center" tabletAlign="start">{heroHeading}</HeadingTextBlock>
              <BodyText variant="medium" align="center" colour="normal-contrast">
                {heroSubheading}
              </BodyText>
              <PreviewInfoWrapper>
                <BodyText variant="medium" weight="bold" colour="highlight-primary">
                  {previewPillCopy}
                </BodyText>
                <BodyText variant="medium" colour="normal-contrast">
                  {subheading}
                </BodyText>
              </PreviewInfoWrapper>
              <MobileStatCards>
                <LuxPlusStatCards saveAmount={saveAmount} firstName={firstName} personalSaving={personalSaving} onPastBookingsClick={handlePastBookingsClick} />
              </MobileStatCards>
              <TextButton kind="primary" disabled={!hasSubscriptionOffers} onClick={handleBecomeMember}>
                {becomeMemberLabel}
              </TextButton>
              {isSpoofed && hasJoinFeeFreeOffer && <TextButton kind="primary" onClick={handleBecomeMemberNoJoinFee}>
                {becomeMemberNoJoinFeeLabel}
              </TextButton>}
              <CSSBreakpoint max="mobile">
                <Footnote variant="medium" colour="normal-contrast">
                  {footnoteText}
                </Footnote>
              </CSSBreakpoint>
            </LeftColumn>
            <StatCardsWrapper>
              <LuxPlusStatCards saveAmount={saveAmount} firstName={firstName} personalSaving={personalSaving} onPastBookingsClick={handlePastBookingsClick} />
              <CSSBreakpoint min="mobile">
                <Footnote variant="medium" colour="normal-contrast">
                  {footnoteText}
                </Footnote>
              </CSSBreakpoint>
            </StatCardsWrapper>
          </HeroGrid>
        </HeroLayoutContainer>
      </HeroSheet>

      {promoBanners.length > 0 && <LuxPlusPromoBannerCarousel items={promoBanners} />}

      <NavSection shadow={!notSticky ? 'bottom-small' : undefined}>
        <HeroLayoutContainer variant="medium">
          <Group direction="horizontal" horizontalAlign="center">
            <ViewToggle>
              <ToggleButton
                className={clsx({ active: activeTab === 'about' })}
                onClick={handleTabAbout}
                type="button"
              >
                {aboutTabLabel}
              </ToggleButton>
              <ToggleButton
                className={clsx({ active: activeTab === 'benefits' })}
                onClick={handleTabBenefits}
                type="button"
              >
                {benefitsTabLabel}
              </ToggleButton>
              <ToggleButton
                className={clsx({ active: activeTab === 'partner-offers' })}
                onClick={handleTabPartnerOffers}
                type="button"
              >
                {partnerOffersTabLabel}
              </ToggleButton>
            </ViewToggle>
          </Group>
        </HeroLayoutContainer>
      </NavSection>
      <div ref={stickyRef} />

      <LuxPlusLandingNavigationProvider navigateToPartnerOffers={handleTabPartnerOffers} scrollOffset={STICKY_NAV_SCROLL_OFFSET}>
        {activeTab === 'about' && <LuxPlusLandingAboutSection />}
        {activeTab === 'benefits' && <LuxPlusLandingBenefitsSection />}
        {activeTab === 'partner-offers' && <LuxPlusLandingPartnerOffersTabSection />}
      </LuxPlusLandingNavigationProvider>
    </>
  )
}

export default LuxPlusFreePreviewLandingPageContent
